import {
  Media, MediaSize, MediaContentTypeType, MediaClazzType, MediaLocation, MediaResolved, MediaClazzTypeValues
} from './medias.models';


export const MEDIAS_MAX_SIZE = Number.MAX_SAFE_INTEGER;

export class MediasHelpers {

  /**
   * Función que devuelve la lista de medias para una localización concreta.
   * Los medias se devuelven con el modelo MediaResolved y su path (ruta) se devuelve completamente calculada, lista
   * para ser renderizada.
   *
   * @param {Media[]} medias Lista de medias de un producto
   * @param {MediaLocation} location Indica en qué localización (widget) queremos devolver los medias
   * @param {number} [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el
   * tamaño que más se ajuste
   * @param {MediaClazzType} [clazzType] Indica si queremos devolver solo los medias de un clazz concreto
   * @param {MediaContentTypeType} [contentTypeType] Indica si queremos devolver solo las imágenes o los videos
   * @param {number} set Set you want to retrieve the medias for, defaults to the first set on the media array
   * @param {string[]} [mediaOriginalNames] Conjunto de nombres utilizados para filtrar a la hora de obtener un estilo
   * de media.
   */
  public static mediasResolved(
    medias: Media[],
    location: MediaLocation,
    width?: number,
    clazzType?: MediaClazzType,
    contentTypeType?: MediaContentTypeType,
    set?: number,
    mediaOriginalNames?: string[]
  ): MediaResolved[] {
    set = set ?? medias[0]?.set;

    const filteredMedias = this.filterMedias(medias, location, set, contentTypeType, mediaOriginalNames, clazzType);
    const mediasResolved = this.processMedias(filteredMedias, location, width);

    mediasResolved.sort((m1, m2) => ((m1.position > m2.position) ? 1 : -1));

    return mediasResolved;
  }

  /**
   * Función que devuelve el primer media de la lista de medias para una localización concreta.
   * El media se devuelve con el modelo MediaResolved y su path (ruta) se devuelve completamente calculada, lista para
   * ser renderizada.
   *
   * @param {Media[]} medias Lista de medias de un producto
   * @param {MediaLocation} location Indica en qué localización (widget) queremos devolver los medias
   * @param {number} [width] Tamaño que va a ocupar el media, por defecto el tamaño máximo posible. Se devolverá el
   * tamaño que más se ajuste
   * @param {MediaClazzType} [clazzType] Indica si queremos devolver solo los medias de un clazz concreto
   * @param {MediaContentTypeType} [contentTypeType] Indica si queremos devolver solo las imágenes o los vídeos
   * @param {number} set Set you want to retrieve the medias for, defaults to the first set on the media array
   * @param {string[]} [mediaOriginalNames] Conjunto de nombres utilizados para filtrar a la hora de obtener un estilo
   * de media.
   */
  public static firstMediaResolved(
    medias: Media[],
    location: MediaLocation,
    width?: number,
    clazzType?: MediaClazzType,
    contentTypeType?: MediaContentTypeType,
    set?: number,
    mediaOriginalNames?: string[]
  ): MediaResolved | undefined {
    const mediasResolved = this.mediasResolved(
      medias, location, width, clazzType, contentTypeType, set, mediaOriginalNames
    );
    const firstMediaResolved = mediasResolved.length > 0
      ? mediasResolved[0]
      : this.loadMainMedia(medias, location, width, contentTypeType, set);
    return firstMediaResolved;
  }

  // ---- Private functions ----
  private static existsNonFallbackEquivalent(mediasResolved: MediaResolved[], media: Media) {
    const hasEquivalentNonFallBackMedia = mediasResolved.some((m) => m.location === media.location
      && m.position === media.position
    // m.clazz === media.clazz && // TODO: comment this until we manage all fallbacks
      && m.contentType.type === media.contentType.type
      && !m.isFallback);

    return hasEquivalentNonFallBackMedia;
  }

  private static calculateMediaResolved(
    media: Media,
    location: MediaLocation,
    width?: number
  ): MediaResolved | undefined {
    let mediaResolved: MediaResolved | undefined;

    const isFallback = media.isFallback;
    const clazz = media.clazz;
    const contentType = media.contentType;
    const metadata = media.metadata;
    const position = media.position;
    const extraInfo = media.extraInfo;
    let path = media.path;
    const url = media.url;

    if (isDefined(media.sizes)) {
      width = width || MEDIAS_MAX_SIZE;
      const size = this.calculateClosestSize(media.sizes, width);
      if (isDefined(size)) {
        path = media.path.replace('{{sizeCode}}', size.code);
        mediaResolved = {
          path, isFallback, clazz, contentType, location, position, size, metadata, extraInfo, url
        };
      }
    } else {
      mediaResolved = {
        path, isFallback, clazz, contentType, location, position, metadata, extraInfo, url
      };
    }
    return mediaResolved;
  }

  private static calculateClosestSize(sizes: MediaSize[], desiredWidth: number): MediaSize | undefined {
    let closestSize: MediaSize | undefined;
    let closestDiff = MEDIAS_MAX_SIZE;

    for (const size of sizes) {
      const currentDiff = Math.abs(size.width - desiredWidth);
      if (currentDiff < closestDiff) {
        closestDiff = currentDiff;
        closestSize = size;
      }
    }

    return closestSize;
  }

  private static loadMainMedia(
    medias: Media[],
    location: MediaLocation,
    width?: number,
    contentTypeType?: MediaContentTypeType,
    set?: number
  ): MediaResolved | undefined {
    return this.mediasResolved(medias, location, width, undefined, contentTypeType, set)[0];
  }

  private static filterMedias(
    medias: Media[],
    location: MediaLocation,
    set: number | undefined,
    contentTypeType: MediaContentTypeType | undefined,
    mediaOriginalNames: string[] | undefined,
    clazzType: MediaClazzType | undefined
  ): Media[] {
    const hasToFilterByClazz = location !== 'custom_image_location';

    let filteredMedias = medias.filter((itMedia) => itMedia.location === location);

    if (set != null) {
      filteredMedias = filteredMedias.filter((itMedia) => itMedia.set === set);
    }

    if (isDefined(contentTypeType)) {
      filteredMedias = filteredMedias.filter((itMedia) => itMedia.contentType.type === contentTypeType);
    }

    if (mediaOriginalNames) {
      filteredMedias = this.filterByOriginalNames(filteredMedias, mediaOriginalNames);
    } else if (isDefined(clazzType) && hasToFilterByClazz) {
      filteredMedias = this.filterByClazzType(filteredMedias, clazzType);
    }

    return filteredMedias;
  }

  private static filterByOriginalNames(medias: Media[], mediaOriginalNames: string[]): Media[] {
    for (const originalName of mediaOriginalNames) {
      const filteredByName = medias.filter((itMedia) => itMedia.extraInfo?.originalName === originalName);
      if (filteredByName.length > 0) {
        return filteredByName;
      }
    }
    return medias;
  }

  private static filterByClazzType(medias: Media[], clazzType: MediaClazzType): Media[] {
    return medias.filter((itMedia) => ((clazzType in MediaClazzTypeValues)
      ? itMedia.clazz.type === clazzType
      : itMedia.path.includes(clazzType)));
  }

  private static processMedias(medias: Media[], location: MediaLocation, width?: number): MediaResolved[] {
    const mediasResolved: MediaResolved[] = [];

    for (const media of medias) {
      if (this.shouldSkipFallback(media, mediasResolved)) {
        continue;
      }

      const mediaResolved = this.calculateMediaResolved(media, location, width);

      if (isDefined(mediaResolved)) {
        mediasResolved.push(mediaResolved);
      }
    }

    return mediasResolved;
  }

  private static shouldSkipFallback(media: Media, mediasResolved: MediaResolved[]): boolean {
    return media.isFallback && this.existsNonFallbackEquivalent(mediasResolved, media);
  }

}
